'use strict';

var _ = require('lodash');
var LogitSharesNormalizationService = function LogitSharesNormalizationService(calibrationAnalysisConstants, momentService) {
  function normalizeShares(logitShares, chartType, adjustedLogit) {
    if (calibrationAnalysisConstants.CHART_TYPES.TIME_OF_DAY === chartType) {
      return normalizeTimeOfDayLogitShares(logitShares);
    }
    if (_.isUndefined(adjustedLogit)) {
      return [];
    }
    return normalizeElapsedTimeAndFareShares(logitShares, adjustedLogit);
  }
  function normalizeTimeOfDayLogitShares(todLogitShares) {
    var sumOfUserShares = _.sumBy(todLogitShares, 'userShare');
    var sumOfAdjustedUtilities = 0;
    var sumOfNormalizedShares = 0;
    var sumofEffectiveShares = 0;
    var normalizedShares = _.map(todLogitShares, function (share) {
      var userShare = _.isNull(share.userShare) ? 0 : share.userShare;
      var adjustment = _.isNull(share.userShare) ? share.utility : 0;
      if (sumOfUserShares > 100) {
        userShare = userShare / sumOfUserShares * 100;
      }
      share.adjustment = adjustment;
      sumOfAdjustedUtilities += adjustment;
      share.normalizedShare = userShare;
      sumOfNormalizedShares += userShare;
      if (!_.isNull(share.userShare) && share.userShare !== share.originalUserShare) {
        share._old = {
          userShare: share.originalUserShare
        };
      }
      return share;
    });
    var adjustedShares = _.map(normalizedShares, function (share) {
      share.effectiveShare = share.adjustment === 0 ? share.normalizedShare : (100 - sumOfNormalizedShares) * (share.adjustment / sumOfAdjustedUtilities);
      sumofEffectiveShares += share.effectiveShare;
      return share;
    });
    var noAdjustmentBuckets = _.filter(adjustedShares, function (share) {
      return _.isNull(share.userShare);
    });
    computeAndRedistributeNetShares(adjustedShares, noAdjustmentBuckets, sumofEffectiveShares);
    negateAdjustmentsWhenAllZeroes(adjustedShares, noAdjustmentBuckets, sumOfUserShares);
    negateAdjustmentsWhenAllRemoved(adjustedShares, noAdjustmentBuckets, sumOfUserShares);
  }
  function normalizeElapsedTimeAndFareShares(logitShares, adjustedLogit) {
    var minValue = _.get(_.minBy(logitShares, 'key'), 'key');
    var maxValue = _.get(_.maxBy(logitShares, 'key'), 'key');
    var sumOfUtilities = 0;
    var utilityShares = _.map(logitShares, function (share) {
      var bucketValue = share.key;
      share.adjustedUtility = bucketValue === minValue ? Math.exp(adjustedLogit) : Math.exp((bucketValue - minValue) * adjustedLogit);
      sumOfUtilities += share.adjustedUtility;
      return share;
    });
    _.map(utilityShares, function (share) {
      var bucketValue = share.key;
      var adjustedShare = getAdjustedShare(share.adjustedUtility, sumOfUtilities, bucketValue, maxValue);
      share.userShare = adjustedShare;
      share.effectiveShare = adjustedShare;
      share.normalizedShare = adjustedShare;
      return share;
    });
  }
  function computeAndRedistributeNetShares(adjustedShares, noAdjustmentBuckets, sumofEffectiveShares) {
    if (!(_.isEmpty(noAdjustmentBuckets) && sumofEffectiveShares < 100)) {
      return;
    }
    _.forEach(adjustedShares, function (share) {
      share.effectiveShare = share.effectiveShare / sumofEffectiveShares * 100;
    });
  }
  function negateAdjustmentsWhenAllZeroes(adjustedShares, noAdjustmentBuckets, sumOfUserShares) {
    if (!(_.isEmpty(noAdjustmentBuckets) && sumOfUserShares === 0)) {
      return;
    }
    _.forEach(adjustedShares, function (share) {
      share.effectiveShare = share.modelShare;
    });
  }
  function negateAdjustmentsWhenAllRemoved(adjustedShares, noAdjustmentBuckets, sumOfUserShares) {
    if (!(noAdjustmentBuckets.length === adjustedShares.length && sumOfUserShares === 0)) {
      return;
    }
    _.forEach(adjustedShares, function (share) {
      share.effectiveShare = share.modelShare;
      share.normalizedShare = undefined;
    });
  }
  function transformTodLogitsToShares(timeOfDayData, filteredServices) {
    if (_.isEmpty(timeOfDayData)) {
      return [];
    }
    var utilityData = {
      sumOfModelUtilities: 0,
      sumOfEffectiveUtilities: 0,
      sumOfPublishedModelUtilities: 0,
      sumOfPublishedEffectiveUtilities: 0,
      sumOfScheduledUtilities: 0,
      sumOfScheduledPublishedUtilities: 0
    };
    var moment = momentService.now(momentService.FIELD.DEPARTURE_TIME).setHour(23).setMinute(59);
    return _.chain(timeOfDayData).reduceRight(function (transformedTod, todLogit) {
      //Determine Time of Day start and end to group scheduled services
      var todHour = determineStartEndTimeOfDay(todLogit, moment);
      moment = moment.setHour(todHour - 1);
      return transformedTod.concat(todLogit);
    }, []).reverse().map(function (tod) {
      // Compute Utility values as exponentials of time of day coefficients
      calculateUtilityValuesForTimeOfDay(tod, filteredServices, utilityData);
      return tod;
    }).map(function (tod) {
      //Compute Share values from utilities as utility divided by sum of corresponding utilities.
      calculateShareValuesFromUtilities(tod, utilityData);
      return tod;
    }).value();
  }
  function determineStartEndTimeOfDay(todLogit, moment) {
    var tod = todLogit.timeOfDayStart;
    var todHour = parseInt(tod) / 100;
    var fromTime = momentService.now(momentService.FIELD.DEPARTURE_TIME).setHour(_.toNumber(tod.substring(0, 2))).setMinute(_.toNumber(tod.substring(2, 4)));
    var toTime = moment;
    todLogit.fromTime = fromTime;
    todLogit.toTime = toTime;
    return todHour;
  }
  function calculateUtilityValuesForTimeOfDay(tod, filteredServices, utilityData) {
    var isScheduledTod = isScheduledTimeOfDay(tod, filteredServices);
    tod.modelUtility = _.isUndefined(tod.modelUtility) ? Math.exp(tod.modelTimeOfDayPreferenceCoefficient) : tod.modelUtility;
    tod.utility = tod.modelUtility;
    utilityData.sumOfModelUtilities += tod.modelUtility;
    tod.effectiveUtility = _.isNull(tod.timeOfDayPreferenceCoefficient) ? 0 : Math.exp(tod.timeOfDayPreferenceCoefficient);
    utilityData.sumOfEffectiveUtilities += tod.effectiveUtility;
    tod.publishedUtility = _.isNil(tod.publishedModelTimeOfDayPreferenceCoefficient) ? 0 : Math.exp(tod.publishedModelTimeOfDayPreferenceCoefficient);
    utilityData.sumOfPublishedModelUtilities += tod.publishedUtility;
    tod.publishedEffectiveUtility = _.isNull(tod.publishedTimeOfDayPreferenceCoefficient) ? 0 : Math.exp(tod.publishedTimeOfDayPreferenceCoefficient);
    utilityData.sumOfPublishedEffectiveUtilities += tod.publishedEffectiveUtility;
    if (isScheduledTod) {
      tod.scheduledUtility = tod.modelUtility;
      utilityData.sumOfScheduledUtilities += tod.scheduledUtility;
      tod.publishedScheduledUtility = tod.publishedUtility;
      utilityData.sumOfScheduledPublishedUtilities += tod.publishedScheduledUtility;
    }
  }
  function calculateShareValuesFromUtilities(tod, utilityData) {
    tod.modelShare = _.isUndefined(tod.modelShare) ? tod.modelUtility / utilityData.sumOfModelUtilities * 100 : tod.modelShare;
    tod.userShare = _.isUndefined(tod.userShare) ? tod.userTimeOfDayPreferenceCoefficient : tod.userShare;
    tod.effectiveShare = _.isUndefined(tod.effectiveShare) ? tod.effectiveUtility / utilityData.sumOfEffectiveUtilities * 100 : tod.effectiveShare;
    tod.originalUserShare = _.isUndefined(tod.originalUserShare) ? tod.userShare : tod.originalUserShare;
    tod.originalEffectiveShare = _.isUndefined(tod.originalEffectiveShare) ? tod.effectiveShare : tod.originalEffectiveShare;
    tod.publishedShare = utilityData.sumOfPublishedModelUtilities > 0 ? tod.publishedUtility / utilityData.sumOfPublishedModelUtilities * 100 : null;
    tod.publishedUserShare = utilityData.sumOfPublishedEffectiveUtilities > 0 ? tod.publishedEffectiveUtility / utilityData.sumOfPublishedEffectiveUtilities * 100 : null;
    tod.scheduledShare = utilityData.sumOfScheduledUtilities > 0 ? tod.scheduledUtility / utilityData.sumOfScheduledUtilities * 100 : null;
    tod.publishedScheduledShare = utilityData.sumOfScheduledPublishedUtilities > 0 ? tod.publishedScheduledUtility / utilityData.sumOfScheduledPublishedUtilities * 100 : null;
  }
  function isScheduledTimeOfDay(todLogit, filteredServices) {
    if (_.isNil(filteredServices) || _.isEmpty(filteredServices)) {
      return false;
    }
    var scheduledService = _.filter(filteredServices, function (service) {
      var departureTime = momentService.now(momentService.FIELD.DEPARTURE_TIME).setHour(service.departureTime.hour()).setMinute(service.departureTime.minutes());
      return departureTime.isSame(todLogit.fromTime) || departureTime.isSame(todLogit.toTime) || departureTime.isBetween(todLogit.fromTime, todLogit.toTime);
    });
    return !_.isEmpty(scheduledService);
  }
  function transformFareElapsedTimeLogitsToShare(logitData, services, scheduledServices, bucketSize, chartType) {
    var fixedShares = computeLogitSharesForFareAndElapsedTime(logitData, services, bucketSize, chartType, true);
    var scheduledShares = computeLogitSharesForFareAndElapsedTime(logitData, scheduledServices, bucketSize, chartType, false);
    _.map(fixedShares, function (share) {
      var scheduledData = _.filter(scheduledShares, function (scheduledShare) {
        return scheduledShare.key <= share.rangeEnd && scheduledShare.key >= share.key;
      });
      share.scheduledShare = _.sumBy(scheduledData, 'modelShare');
      share.publishedScheduledShare = _.sumBy(scheduledData, 'publishedShare');
    });
    return fixedShares;
  }
  function computeLogitSharesForFareAndElapsedTime(logitData, services, bucketSize, chartType, normalizeBucketSize) {
    var key = chartType === calibrationAnalysisConstants.CHART_TYPES.FARE ? 'fare' : 'elapsedTime';
    var sortedServices = _.chain(services).filter(function (service) {
      return !_.isNaN(_.get(service, key));
    }).sortBy(key).value();
    if (_.isEmpty(sortedServices)) {
      if (!normalizeBucketSize) {
        return [];
      }
      sortedServices = getDefaultServices(bucketSize, key);
    }
    var min = _.get(sortedServices[0], key);
    var max = _.get(sortedServices[sortedServices.length - 1], key);
    if (normalizeBucketSize) {
      var normalizedMaxAndBucket = normalizeMaxBucketSize(min, max, bucketSize);
      max = normalizedMaxAndBucket.max;
      bucketSize = normalizedMaxAndBucket.bucketSize;
    }
    var shareUtilities = [];
    var sumOfModelUtilities = 0;
    var sumOfUserUtilities = 0;
    var sumOfPublishedModelUtilities = 0;
    var sumOfPublishedUserUtilities = 0;
    var computeShares = true;
    var token = min;
    while (computeShares) {
      if (token >= max) {
        token = max;
        computeShares = false;
      }
      var modelUtility = computeUtility(logitData.modelLogit, min, token);
      sumOfModelUtilities += modelUtility;
      var userUtility = null;
      if (!_.isNil(logitData.userLogit)) {
        userUtility = computeUtility(logitData.userLogit, min, token);
        sumOfUserUtilities += userUtility;
      }
      var publishedUtility = null;
      if (!_.isNil(logitData.publishedModelLogit)) {
        publishedUtility = computeUtility(logitData.publishedModelLogit, min, token);
        sumOfPublishedModelUtilities += publishedUtility;
      }
      var publishedUserUtility = null;
      if (!_.isNil(logitData.publishedUserLogit)) {
        publishedUserUtility = computeUtility(logitData.publishedUserLogit, min, token);
        sumOfPublishedUserUtilities += publishedUserUtility;
      }
      var bucketRangeEnd = token + (bucketSize - 1);
      if (bucketRangeEnd >= max) {
        bucketRangeEnd = max - 1;
      }
      if (bucketRangeEnd < token) {
        bucketRangeEnd = token + 1;
      }
      var utility = {
        userUtility: userUtility,
        modelUtility: modelUtility,
        publishedUtility: publishedUtility,
        publishedUserUtility: publishedUserUtility,
        key: token,
        rangeEnd: bucketRangeEnd
      };
      shareUtilities.push(utility);
      token += bucketSize;
    }
    return _.map(shareUtilities, function (util) {
      util.modelShare = getAdjustedShare(util.modelUtility, sumOfModelUtilities, util.key, max);
      util.userShare = getAdjustedShare(util.userUtility, sumOfUserUtilities, util.key, max);
      util.effectiveShare = _.isNull(util.userShare) ? util.modelShare : util.userShare;
      util.originalUserShare = util.userShare;
      util.originalEffectiveShare = util.effectiveShare;
      util.publishedShare = getAdjustedShare(util.publishedUtility, sumOfPublishedModelUtilities, util.key, max);
      util.publishedUserShare = getAdjustedShare(util.publishedUserUtility, sumOfPublishedUserUtilities, util.key, max);
      return util;
    });
  }
  function getDefaultServices(bucketSize, key) {
    var defaultServices = [];
    var defaultService = {};
    _.set(defaultService, key, bucketSize);
    defaultServices.push(defaultService);
    return defaultServices;
  }
  function getAdjustedShare(utility, sumOfUtilities, bucketValue, maxValue) {
    var adjustedShare;
    if (_.isNil(utility)) {
      return null;
    }
    if (_.isEqual(sumOfUtilities, Infinity)) {
      if (bucketValue === maxValue) {
        return 100;
      } else {
        return 0;
      }
    }
    adjustedShare = utility / sumOfUtilities * 100;
    return adjustedShare;
  }
  function computeUtility(logitValue, min, token) {
    return min === token ? Math.exp(logitValue) : Math.exp((token - min) * logitValue);
  }
  function normalizeMaxBucketSize(min, max, bucketSize) {
    var configuredMinimum = calibrationAnalysisConstants.CHART_BUCKET_SETTINGS.MIN;
    var configuredMaximum = calibrationAnalysisConstants.CHART_BUCKET_SETTINGS.MAX;
    var roundOffFactor = calibrationAnalysisConstants.CHART_BUCKET_SETTINGS.ROUND_OFF_FACTOR;
    var bucketCount = 0;
    var num = min;
    while (num <= max) {
      bucketCount++;
      num += bucketSize;
    }
    if (bucketCount < configuredMinimum) {
      max = min + bucketSize * (configuredMinimum - 1);
    } else if (bucketCount >= configuredMaximum) {
      var adjustedBucketSize = _.ceil(_.ceil((max - min) / (configuredMaximum - 1)) / roundOffFactor);
      bucketSize = adjustedBucketSize * roundOffFactor;
    }
    return {
      max: max,
      bucketSize: bucketSize
    };
  }
  function transformTodSharesToLogits(logitParams) {
    _.map(logitParams, function (logitParam) {
      var logitTimeOfDayPreferences = logitParam.logitTimeOfDayPreferences;
      var sumOfUtilities = _.sumBy(logitTimeOfDayPreferences, 'modelUtility');
      var normalizedShares = _.find(logitTimeOfDayPreferences, function (todPref) {
        return !_.isNil(todPref.normalizedShare);
      });
      var isNormalized = !_.isUndefined(normalizedShares);
      if (isNormalized) {
        _.chain(logitTimeOfDayPreferences).map(function (todPref) {
          todPref.adjustedUserUtility = todPref.effectiveShare / 100 * sumOfUtilities;
          return todPref;
        }).map(function (todPref) {
          todPref.userTimeOfDayPreferenceCoefficient = todPref.userShare;
          todPref.timeOfDayPreferenceCoefficient = Math.log(todPref.adjustedUserUtility);
          return todPref;
        }).value();
      }
    });
  }
  function transformLogits(reviewData, contextData, services, scheduledServices, logitType) {
    var data = {};
    if (logitType === calibrationAnalysisConstants.CHART_TYPES.TIME_OF_DAY) {
      data.logitShares = transformTodLogitsToShares(reviewData.logitTimeOfDayPreferences, scheduledServices);
    } else if (logitType === calibrationAnalysisConstants.CHART_TYPES.TRAVEL_TIME) {
      var logitData = getLogitDataObject(reviewData.modelElapsedTimeLogit, reviewData.userElapsedTimeLogit, reviewData.publishedModelElapsedTimeLogit, reviewData.publishedUserElapsedTimeLogit);
      data.logitShares = transformFareElapsedTimeLogitsToShare(logitData, services, scheduledServices, contextData.timeBucketSize, logitType);
      data.userLogit = logitData.userLogit;
    } else if (logitType === calibrationAnalysisConstants.CHART_TYPES.FARE) {
      var fareLogitData = getLogitDataObject(reviewData.modelRelativeFareLogit, reviewData.userRelativeFareLogit, reviewData.publishedModelFareLogit, reviewData.publishedUserFareLogit);
      data.logitShares = transformFareElapsedTimeLogitsToShare(fareLogitData, services, scheduledServices, contextData.fareBucketSize, logitType);
      data.userLogit = fareLogitData.userLogit;
    }
    return data;
  }
  function getLogitDataObject(modelLogit, userLogit, publishedLogit, publishedUserLogit) {
    return {
      modelLogit: modelLogit,
      userLogit: userLogit,
      publishedModelLogit: publishedLogit,
      publishedUserLogit: publishedUserLogit
    };
  }
  return {
    transformLogits: transformLogits,
    normalizeShares: normalizeShares,
    transformTodLogitsToShares: transformTodLogitsToShares,
    transformFareElapsedTimeLogitsToShare: transformFareElapsedTimeLogitsToShare,
    transformTodSharesToLogits: transformTodSharesToLogits
  };
};
LogitSharesNormalizationService.$inject = ['CalibrationAnalysisConstants', 'MomentService'];
module.exports = LogitSharesNormalizationService;